import { IColumn, IGetColumnsResponse } from "../../types/ColumnTypes";
import {
  appendColumns,
  selectColumn,
  setColumns,
  setColumnsFailed,
  setColumnsLoading,
  setHasMore,
  touchColumns,
} from "../../redux/reducers/columnReducer";
import {
  getAllColumnsStories,
  getColumnById,
} from "../../services/columnService";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { isCacheFresh } from "./cachePolicy";
import { selectUser } from "../../redux/reducers/userReducer";

export const useColumns = () => {
  const dispatch = useDispatch();
  const column = useSelector(selectColumn);
  const user = useSelector(selectUser);

  const email = user?.data?.user?.user_id || "";
  const revalidating = useRef(false);

  const fetchPage = useCallback(
    async (page: number, startAfterId?: string): Promise<IGetColumnsResponse> => {
      return getAllColumnsStories({ page, email, startAfterId });
    },
    [email],
  );

  // Called by usePagination on scroll-bottom. Its internal counter resets on
  // every mount, so ignore the page it passes and continue from the slice's
  // cursor — with a warm cache holding N pages, the next fetch is page N+1.
  const fetchColumnData = useCallback(
    async (_page?: number) => {
      if (!column.hasMore) return;
      const page = column.currentPage + 1;
      const lastId = column.columns[column.columns.length - 1]?.id;
      try {
        const data = await fetchPage(page, lastId);
        dispatch(appendColumns({ columns: data.data.columns, page }));
        dispatch(setHasMore(data.data.hasMore));
      } catch (error) {
        dispatch(setColumnsFailed());
        console.log("Error fetching columns");
      }
    },
    [dispatch, fetchPage, column.currentPage, column.hasMore],
  );

  useEffect(() => {
    const cacheValid =
      column.columns.length > 0 && column.cachedForEmail === email;

    const run = async () => {
      if (!cacheValid) {
        // Cold start (or the user changed): the page renders a skeleton view
        // while status === 'loading' — no global spinner.
        try {
          dispatch(setColumnsLoading());
          const data = await fetchPage(1);
          dispatch(setColumns({ columns: data.data.columns, email, page: 1 }));
          dispatch(setHasMore(data.data.hasMore));
        } catch (error) {
          dispatch(setColumnsFailed());
          console.log("Error fetching columns");
        }
        return;
      }

      // Warm cache: render instantly, revalidate silently. Only replace the
      // cache (and reset scrolled pages) when new content actually exists.
      // Skip the network entirely while the cache is fresh.
      if (isCacheFresh(column.lastFetched)) return;
      if (revalidating.current) return;
      revalidating.current = true;
      try {
        const data = await fetchPage(1);
        const freshFirstId = data.data.columns?.[0]?.id;
        const cachedFirstId = column.columns[0]?.id;
        if (freshFirstId && freshFirstId !== cachedFirstId) {
          dispatch(setColumns({ columns: data.data.columns, email, page: 1 }));
          dispatch(setHasMore(data.data.hasMore));
        } else {
          dispatch(touchColumns());
        }
      } catch (error) {
        // Background refresh failing is invisible; keep serving the cache.
      } finally {
        revalidating.current = false;
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  return {
    stories: column.columns,
    fetchColumnData,
    hasMore: column.hasMore,
    // Skeleton state: only on a true cold start (nothing cached yet).
    isLoading: column.status === "loading" && column.columns.length === 0,
  };
};

export const useColumnById = (id: string) => {
  const [story, setStory] = useState<IColumn | null>(null);

  const fetchData = async () => {
    try {
      const data = await getColumnById({
        columnId: id,
      });
      console.log('COLUMN', data);
      setStory(data.data);
    } catch (error) {
      console.log('Error fetching columns');
    }
  };
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { story };
};
